import type { PersistedQuoteEditorState } from '@shared/quote-editor-state';
import type { Position, QuoteHeader, QuoteItem } from '@shared/schema';
import type { QuotePersistenceIdentity } from './store';
import type { QuoteDataPackage, SaveQuoteRequest } from './quote-data-service';

export type QuoteSaveMode = 'save' | 'save-as';

export interface QuoteSaveSnapshot {
  header: QuoteHeader;
  items: QuoteItem[];
  positions: Position[];
  gewerkOrder: string[];
  editorState: PersistedQuoteEditorState;
}

function assertValidSlot(slot: number): void {
  if (!Number.isSafeInteger(slot) || slot < 1 || slot > 10_000) {
    throw new RangeError('Invalid quote slot: expected an integer from 1 to 10000.');
  }
}

function assertValidExpectedStorageRevision(storageRevision: number): void {
  if (!Number.isSafeInteger(storageRevision) || storageRevision < 0) {
    throw new RangeError('Invalid storageRevision: expected a non-negative safe integer.');
  }
}

function assertValidPersistedStorageRevision(storageRevision: number): void {
  if (!Number.isSafeInteger(storageRevision) || storageRevision < 1) {
    throw new RangeError('Invalid persisted storageRevision: expected a positive safe integer.');
  }
}

export function buildQuoteSaveRequest(
  snapshot: QuoteSaveSnapshot,
  identity: QuotePersistenceIdentity | null,
  mode: QuoteSaveMode,
): SaveQuoteRequest {
  const request: SaveQuoteRequest = {
    header: snapshot.header,
    items: snapshot.items,
    positions: snapshot.positions,
    gewerkOrder: snapshot.gewerkOrder,
    editorState: snapshot.editorState,
  };

  if (mode === 'save' && identity !== null) {
    assertValidSlot(identity.slot);
    request.targetSlot = identity.slot;

    assertValidExpectedStorageRevision(identity.storageRevision);
    request.expectedStorageRevision = identity.storageRevision;
  }

  return request;
}

export function getSavedQuotePersistenceIdentity(
  savedQuote: QuoteDataPackage,
): QuotePersistenceIdentity {
  const { slot, storageRevision } = savedQuote.metadata;

  if (slot === undefined) {
    throw new Error('Save response is missing slot.');
  }
  if (storageRevision === undefined) {
    throw new Error('Save response is missing storageRevision.');
  }

  assertValidSlot(slot);
  assertValidPersistedStorageRevision(storageRevision);

  return {
    slot,
    storageRevision,
  };
}

export function bindQuotePersistenceResponse(
  savedQuote: QuoteDataPackage,
  bind: (identity: QuotePersistenceIdentity) => void,
): QuotePersistenceIdentity {
  const identity = getSavedQuotePersistenceIdentity(savedQuote);
  bind(identity);
  return identity;
}
